/**
 * CheckpointHeader - Header component for checkpoint conversations
 *
 * Contains:
 * - Petal reward badge centered, taking most horizontal space
 * - Close button positioned on the right
 */
import React, { useContext } from "react"
import { View, StyleSheet, TouchableOpacity } from "react-native"
import Animated, { FadeIn, BounceIn } from "react-native-reanimated"
import { FontAwesome5 } from "@expo/vector-icons"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import PetalRewardBadge from "../../PetalRewardBadge"

/**
 * @param {Object} props
 * @param {boolean} props.showCloseButton - Whether to show the close button
 * @param {Function} props.onClose - Close button callback
 * @param {boolean} props.showPetalBadge - Whether to show the petal badge
 * @param {boolean} props.isPetalEarned - Whether the petal has been earned
 * @param {number} props.progress - Progress value for petal badge (response count)
 * @param {number} props.width - Optional explicit width for the header
 */
const CheckpointHeader = ({
    showCloseButton = false,
    onClose,
    showPetalBadge = true,
    isPetalEarned = false,
    progress = 0,
    width: propWidth
}) => {
    const dimensions = useContext(DimensionsContext)
    const headerWidth = propWidth || (dimensions?.width || 300) - 40

    // Calculate progress bar width: take most of the space, leaving room for petal icon and close button
    // Petal icon ~36px, close button ~44px, gaps ~20px = ~100px reserved
    const progressBarWidth = Math.max(headerWidth - 100, 120)

    return (
        <View style={[styles.headerSection, { width: headerWidth }]}>
            {/* Close button - positioned on the left */}
            {showCloseButton && (
                <Animated.View entering={FadeIn.duration(300)} style={styles.closeButtonWrapper}>
                    <TouchableOpacity
                        style={styles.closeButton}
                        onPress={onClose}
                        activeOpacity={0.8}
                    >
                        <View style={styles.closeButtonContent}>
                            <FontAwesome5
                                name="times"
                                size={20}
                                color={theme.colors.primary}
                            />
                        </View>
                    </TouchableOpacity>
                </Animated.View>
            )}

            {/* Petal badge - centered, taking most horizontal space */}
            {showPetalBadge && (
                <Animated.View
                    entering={BounceIn.duration(600).delay(200)}
                    style={styles.petalContainer}
                >
                    <PetalRewardBadge
                        isEarned={isPetalEarned}
                        progress={progress}
                        barWidth={progressBarWidth}
                        onEarnAnimationComplete={() => {}}
                    />
                </Animated.View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    headerSection: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
        position: "relative"
    },
    petalContainer: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 50,
    },
    closeButtonWrapper: {
        position: "absolute",
        left: 0
    },
    closeButton: {
        borderRadius: 30,
        overflow: "hidden"
    },
    closeButtonContent: {
        padding: 12
    }
})

export default CheckpointHeader
